import { apiGet, apiPatch, apiPost } from "../api";
import { normalizePushBilling } from "./push";
import { extendStudioCatalog } from "./studioCatalog";
import type { ApprovalQueue, ApprovalRules } from "./approval";
import type { CampaignStatusGroup } from "./campaignList";
import type {
  BillingOp,
  ClientAccessGrant,
  ClientAccessProvisionResult,
  ClientsAccessResponse,
  MarketingBootstrap,
  MarketingCampaign,
  MarketingClientsResponse,
  MarketingDraft,
  MarketingPreview,
  TemplatesResponse,
  Topup,
  TopupResend,
  PromoPool,
  PromoImportResult,
  PromoCodeList,
  PushStatus,
  CampaignSaveResult,
} from "./types";

export const marketingKeys = {
  bootstrap: ["marketing", "bootstrap"] as const,
  templates: ["marketing", "templates"] as const,
  campaigns: ["marketing", "campaigns"] as const,
  topups: ["marketing", "topups"] as const,
  promoPools: ["marketing", "promo-pools"] as const,
  clients: ["marketing", "clients"] as const,
  frequencyCap: ["marketing", "frequency-cap"] as const,
  cascadeSavings: ["marketing", "cascade-savings"] as const,
  /** Отдельный ключ, чтобы обновление списка клиентов не дёргало справочник кабинетов. */
  clientsAccess: ["marketing", "clients-access"] as const,
  billingOps: ["billing", "ops"] as const,
  /** Очередь «На согласовании» и настройки согласования (пакет 7b, п.23). */
  approvals: ["marketing", "approvals"] as const,
  approvalSettings: ["marketing", "approval-settings"] as const,
};

export function queryString(
  filters: Record<string, string | undefined>,
): string {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value) params.set(key, value);
  });
  return params.size ? `?${params}` : "";
}

/** Сетевой лимит частоты (пакет 7, п.21): GET/PATCH /api/marketing/frequency-cap. */
export interface FrequencyCapPolicy {
  days: number;
  sms: number;
  email: number;
  push: number;
  all: number;
}
export interface FrequencyCapState extends FrequencyCapPolicy {
  /** Рекомендуемые значения (3 SMS и 5 сообщений за 7 дней); без настройки лимит выключен. */
  recommended: FrequencyCapPolicy;
  /** Правит только администратор сети с правами на каналы и на управление. */
  editable: boolean;
  /** Последние изменения политики (новые сверху); старые сервера поля не присылают. */
  history?: FrequencyCapHistoryEntry[];
}
/** Одна запись журнала: какое поле политики меняли и с какого значения на какое. */
export interface FrequencyCapHistoryEntry {
  at: string;
  actor: string;
  /** days | sms | email | push | all */
  field: string;
  oldValue: string;
  newValue: string;
}
/** «Сэкономлено SMS каскадом» (пакет 7, п.32): GET /api/marketing/cascade-savings. */
export interface CascadeSavings {
  guests: number;
  runs: number;
  from: string;
  to: string;
  /** Оценка по цене одной части SMS; null — цена канала неизвестна. */
  estimatedSaved: string | null;
  smsPrice: string | null;
}

export const marketingApi = {
  frequencyCap: (signal?: AbortSignal) =>
    apiGet<FrequencyCapState>("/api/marketing/frequency-cap", signal),
  saveFrequencyCap: (policy: FrequencyCapPolicy) =>
    apiPatch<FrequencyCapState>("/api/marketing/frequency-cap", policy),
  /** Сколько гостей кампании отсеяно сетевым лимитом (и сам лимит). */
  campaignFrequencyCap: (id: string, signal?: AbortSignal) =>
    apiGet<FrequencyCapPolicy & { skipped: number }>(
      `/api/marketing/campaigns/${encodeURIComponent(id)}/frequency-cap`,
      signal,
    ),
  cascadeSavings: (
    filters: { from?: string; to?: string; campaignId?: string; companyId?: string },
    signal?: AbortSignal,
  ) =>
    apiGet<CascadeSavings>(
      `/api/marketing/cascade-savings${queryString(filters)}`,
      signal,
    ),
  completeOnboarding: () => apiPost<{ onboardingCompleted: boolean }>("/api/marketing/onboarding/complete", {}),
  pushStatus: (signal?: AbortSignal) =>
    apiGet<PushStatus>("/api/marketing/push/status", signal),
  sharePromoPool: (id: string, companyIds: string[]) =>
    apiPost(`/api/marketing/promo-pools/${encodeURIComponent(id)}/access`, {
      companyIds,
    }),
  promoCodes: (
    id: string,
    filters: { status: string; q: string; page: string; pageSize: string },
    signal?: AbortSignal,
  ) =>
    apiGet<PromoCodeList>(
      `/api/marketing/promo-pools/${encodeURIComponent(id)}/codes${queryString(filters)}`,
      signal,
    ),
  renamePromoPool: (id: string, name: string) =>
    apiPatch<{ name: string }>(
      `/api/marketing/promo-pools/${encodeURIComponent(id)}`,
      { name },
    ),
  managePromoCodes: (
    id: string,
    body: {
      action: "remove" | "restore";
      ids?: string[];
      allAvailable?: boolean;
    },
  ) =>
    apiPost<{ changed: number }>(
      `/api/marketing/promo-pools/${encodeURIComponent(id)}/codes/manage`,
      body,
    ),
  saveBatch: (body: { idempotencyKey: string; campaigns: MarketingDraft[] }) =>
    apiPost<{
      batchId: string;
      campaigns: Array<{
        id: string;
        companyId: string;
        name: string;
        status: "draft";
        version: number;
      }>;
    }>("/api/marketing/campaign-batches", {
      ...body,
      campaigns: body.campaigns.map((draft) => normalizePushBilling(draft)),
    }),
  promoPools: (companyId: string, signal?: AbortSignal) =>
    apiGet<{ pools: PromoPool[] }>(
      `/api/marketing/promo-pools${queryString({ companyId })}`,
      signal,
    ),
  createPromoPool: (body: {
    companyId: string;
    name: string;
    codes: string[];
  }) => apiPost<PromoImportResult>("/api/marketing/promo-pools", body),
  replenishPromoPool: (id: string, codes: string[]) =>
    apiPost<PromoImportResult>(
      `/api/marketing/promo-pools/${encodeURIComponent(id)}/codes`,
      { codes },
    ),
  bootstrap: (signal?: AbortSignal) =>
    apiGet<MarketingBootstrap>("/api/marketing", signal),
  templates: (signal?: AbortSignal) =>
    apiGet<TemplatesResponse>("/api/marketing/templates", signal).then(extendStudioCatalog),
  campaigns: (
    filters: {
      campaignId?: string;
      companyId?: string;
      tenantId?: string;
      channel?: "sms" | "email" | "push";
      mode?: "once" | "automatic";
      statusGroup?: CampaignStatusGroup;
    },
    signal?: AbortSignal,
  ) =>
    apiGet<{ campaigns: MarketingCampaign[] }>(
      `/api/marketing/campaigns${queryString(filters)}`,
      signal,
    ),
  preview: (draft: MarketingDraft, signal?: AbortSignal) =>
    apiPost<MarketingPreview>(
      "/api/marketing/preview",
      normalizePushBilling(draft),
      signal,
    ),
  save: (draft: MarketingDraft, id?: string, version?: number) =>
    id
      ? apiPatch<CampaignSaveResult>(
          `/api/marketing/campaigns/${encodeURIComponent(id)}`,
          { ...normalizePushBilling(draft, true), version },
        )
      : apiPost<CampaignSaveResult>(
          "/api/marketing/campaigns",
          normalizePushBilling(draft),
        ),
  action: (
    id: string,
    action:
      | "launch"
      | "pause"
      | "cancel"
      | "delete"
      | "approve"
      | "return"
      | "withdraw",
    version: number,
    /** Комментарий УК к возврату кампании на доработку (только action return). */
    comment?: string,
  ) =>
    apiPost<{ id: string; status: string; version: number }>(
      `/api/marketing/campaigns/${encodeURIComponent(id)}/action`,
      comment === undefined ? { action, version } : { action, version, comment },
    ),
  /** Очередь «На согласовании»: УК — вся сеть, партнёр — свои кампании. */
  approvals: (signal?: AbortSignal) =>
    apiGet<ApprovalQueue>("/api/marketing/approvals", signal),
  /** Только число кампаний на согласовании (счётчик пункта меню): без определений кампаний. */
  approvalCount: (signal?: AbortSignal) =>
    apiGet<{ total: number; canDecide: boolean }>(
      "/api/marketing/approvals?countOnly=1",
      signal,
    ),
  approvalSettings: (signal?: AbortSignal) =>
    apiGet<ApprovalRules>("/api/marketing/approval/settings", signal),
  saveApprovalSettings: (body: {
    enabled: boolean;
    maxDiscountPercent: number;
    stopWords: string[];
  }) => apiPatch<ApprovalRules>("/api/marketing/approval/settings", body),
  topups: (companyId: string, signal?: AbortSignal) =>
    apiGet<{ topups: Topup[] }>(
      `/api/marketing/topups${queryString({ companyId })}`,
      signal,
    ),
  topup: (body: {
    companyId: string;
    amount: string;
    idempotencyKey: string;
  }) => apiPost<Topup>("/api/marketing/topups", body),
  /** Отменить неоплаченный счёт пополнения (только УК: нужно право отмены счетов CRM). */
  topupCancel: (id: string) =>
    apiPost<Topup>(`/api/marketing/topups/${encodeURIComponent(id)}/cancel`, {}),
  /** Отправить неоплаченный счёт ещё раз на почту; dryRun — только получатели. */
  topupResend: (id: string, dryRun: boolean) =>
    apiPost<TopupResend>(
      `/api/marketing/topups/${encodeURIComponent(id)}/resend${dryRun ? "?dryRun=1" : ""}`,
      {},
    ),
  /** Реселлерский обзор УК: партнёры, их балансы и расходы за период. */
  clients: (filters: { from: string; to: string; pointIds?: string }, signal?: AbortSignal) =>
    apiGet<MarketingClientsResponse>(
      `/api/marketing/clients${queryString(filters)}`,
      signal,
    ),
  /** Состояние кабинетов партнёров. Ошибка запроса не должна ломать экран целиком. */
  clientsAccess: (signal?: AbortSignal) =>
    apiGet<ClientsAccessResponse>("/api/marketing/clients/access", signal),
  grantClientAccess: (partnerId: string, forceReset = false) =>
    apiPost<ClientAccessGrant>(
      `/api/marketing/clients/${encodeURIComponent(partnerId)}/access`,
      { forceReset },
    ),
  provisionMissingAccess: () =>
    apiPost<ClientAccessProvisionResult>(
      "/api/marketing/clients/access/provision-missing",
      {},
    ),
  /** Журнал операций по счёту клиента. */
  /**
   * Журнал счёта. view=summary — для «Последних операций» обзора: списания за
   * сообщения свёрнуты по кампании и дню (Go billingOpsSummarySQL); без него —
   * подробный журнал, как в «Балансе и счетах» и «Счетах партнёров».
   */
  billingOps: (
    accountId: string,
    signal?: AbortSignal,
    view?: "summary",
  ) =>
    apiGet<{ ops: BillingOp[] }>(
      `/api/billing/ops${queryString({ accountId, view })}`,
      signal,
    ),
  /** Оплата за партнёра или корректировка. Повтор с тем же ключом не создаёт вторую операцию. */
  billingTopup: (body: {
    accountId: string;
    amount: string;
    comment: string;
    idempotencyKey: string;
    kind: "topup" | "adjust";
  }) =>
    apiPost<{ ok: boolean; balance: string; duplicate: boolean }>(
      "/api/billing/topup",
      body,
    ),
};
